<template>
  <v-container>
    <!-- META-DATA -->
    <vue-headful
      :title="`${patch.name} | Patch Catalog${this.$store.state.app.meta.titleSuffix}`"
      :description="patch.description + ' | MemerHats printed vinyl patch'" />

    <v-scroll-y-transition
      group
      hide-on-leave
      class="patch-product-wrapper">
      <!-- LOADER -->
      <div
        v-if="loading"
        key="loading"
        class="patch-product-wrapper__loading">
        <v-progress-circular
          size="100"
          indeterminate
          color="primary" />
      </div>

      <!-- IMAGE COLUMN -->
      <div
        v-if="!loading"
        key="image-column"
        class="patch-image__wrapper">
        <img
          :src="imageDir + patch.imageId + '.png'"
          class="patch-image">
      </div>

      <!-- INFO COLUMN -->
      <div
        v-if="!loading"
        key="info-column"
        class="patch-info">
        <div class="patch-info__header-row">
          <h2 class="patch-info__name">
            {{ patch.name }}
          </h2>
          <div class="patch-info__price-wrapper">
            <h3
              class="patch-info__retail-price">
              {{ patchPrice | price }}
            </h3>
          </div>
        </div>
        <div
          v-if="patch.publicId && patch.publicId!=='a-1'"
          class="patch-info__creator-row">
          <span>
            Created By: {{ patch.creatorAlias }}
          </span>
          <span>
            Created On: {{ moment(patch.dateCreated).format('MMM Do, YYYY') }}
          </span>
        </div>

        <v-divider v-if="patchGroup==='submissions'" />

        <div
          v-if="patchGroup==='submissions'"
          class="patch-info__progress-row">
          <v-progress-linear
            v-model="salesProgress"
            height="44"
            color="primary"
            background-color="black"
            class="progress-bar">
            <div class="progress-bar__content">
              <span class="progress-bar__quantity-sold">
                {{ patch.quantitySold }}/{{ salesThreshold }} sold
                <!-- ALT: 'xx% of goal' -->
              </span>
              <span class="progress-bar__time-remaining">
                {{ daysRemaining }} days remaining
              </span>
            </div>
          </v-progress-linear>
        </div>

        <v-divider />

        <div class="patch-info__buy-row">
          <v-btn
            :disabled="patch.quantityInStock === 0"
            large
            color="primary"
            class="patch-info__add-to-cart"
            @click="addToCart">
            {{ addToCartText }}
          </v-btn>

          <v-tooltip
            v-if="patchGroup==='catalog'"
            :disabled="this.$store.getters['auth/isAuthenticated']"
            bottom>
            <span>You must have an account to save items to your favorites list</span>
            <v-btn
              slot="activator"
              flat
              large
              color="primary"
              :loading="favoritesLoading"
              :disabled="!this.$store.getters['auth/isAuthenticated']"
              class="patch-info__add-to-favorites"
              @click="setInFavorites">
              <v-icon>
                {{ patchInFavorites ? 'star' : 'star_border' }}
              </v-icon>
            </v-btn>
          </v-tooltip>
        </div>

        <v-divider />

        <div
          v-if="patch.description"
          class="patch-info__description">
          {{ patch.description }}
        </div>

        <v-divider v-if="patch.description" />

        <div
          v-if="patch.tags"
          class="patch-info__tags">
          <router-link
            v-for="tag in sortedTags"
            :key="tag"
            v-ripple
            class="patch-info__tag"
            :to="{
              name: `patch-${patchGroup}`, // kinda hacky
              params: { tag },
            }">
            {{ tag }}
          </router-link>
        </div>
        <div class="patch-info__shipping-row">
          Usually ships within 1 business day<br>
          Estimated delivery: 2-3 business days
        </div>

        <v-divider />

        <div class="patch-share">
          <!-- url copy hidden input -->
          <input
            :value="patchURL"
            class="patch-share__url-input">
          <v-btn
            flat
            color="primary"
            :loading="copyUrlLoading"
            :disabled="copyUrlLoading"
            @click="copyToClipboard">
            <span slot="loader">copied!</span>
            copy URL
            <v-icon>assignment</v-icon>
          </v-btn>
        </div>
      </div>
    </v-scroll-y-transition>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'PatchProduct',
  components: {
    //
  },
  data() {
    return {
      favoritesLoading: false,
      copyUrlLoading: false,
    };
  },
  computed: {
    patchGroup() {
      return this.$route.params.patchGroup;
    },
    ...mapState('patches', {
      patch: 'patch',
      loading: 'loading',
      errorMessage: 'errorMessage',
      salesThreshold: 'salesThreshold',
      salesPeriod: 'salesPeriod',
    }),
    imageDir() {
      let dir = '';
      if (this.patchGroup === 'catalog') {
        dir = this.$store.state.patches.imageDir;
      }
      if (this.patchGroup === 'submissions') {
        dir = this.$store.state.patches.customsDir;
      }
      return dir;
    },
    sortedTags() {
      return this.patch.tags.map(x => x).sort();
    },
    patchPrice() {
      return this.$store.state.cart.catalogPatchPrice;
    },
    patchInFavorites() {
      if (this.$store.getters['auth/isAuthenticated']) {
        const favoritesStore = this.$store.state.user.currentUser.favorites;
        const itemIndex = favoritesStore.patches.indexOf(this.patch.imageId);
        if (itemIndex >= 0) {
          return true;
        }
        return false;
      }
      return false;
    },
    addToCartText() {
      if (this.patch.quantityInStock === 0) {
        return 'sold out';
      }
      return 'add to cart';
    },
    patchURL() {
      return window.location.href;
    },
    salesProgress() {
      return (this.patch.quantitySold / this.salesThreshold) * 100;
    },
    daysRemaining() {
      const submitDate = this.moment(this.patch.dateSubmitted);
      const endDate = submitDate.add(this.salesPeriod, 'days');
      const today = this.moment();
      const daysRemaining = endDate.diff(today, 'days');
      return daysRemaining;
    },
  },
  beforeRouteUpdate(to, from, next) {
    // update data when redirecting:
    // uses same component so created() isn't called
    const { id } = to.params;
    const { patchGroup } = to.params;
    this.$store
      .dispatch('patches/getPatchById', { id, patchGroup })
      .catch(() => {
        this.$router.push('/404');
      });
    next();
  },
  created() {
    this.$store.dispatch('patches/getSubmissionsConfig');

    const { id } = this.$route.params;
    const { patchGroup } = this.$route.params;
    this.$store
      .dispatch('patches/getPatchById', { id, patchGroup })
      .catch(() => {
        // try redirecting to catalog if submission not found
        if (patchGroup === 'submissions') {
          this.$router.push({
            name: 'patch-product',
            params: {
              patchGroup: 'catalog',
              id,
            },
          });
        } else {
          this.$router.push('/404');
        }
      });
  },
  methods: {
    copyToClipboard() {
      this.copyUrlLoading = true;
      const text = document.querySelector('.patch-share__url-input');
      text.select();
      document.execCommand('copy');
      setTimeout(() => (this.copyUrlLoading = false), 1000);
    },
    addToCart() {
      const { patch } = this;
      const { patchGroup } = this;
      this.$store.dispatch('cart/addPatchToCart', { patch, patchGroup });
      this.$store.dispatch('app/openMenu', 'cartDrawer');
    },
    setInFavorites() {
      const itemSet = 'patches';
      const itemId = this.patch.imageId;
      this.favoritesLoading = true;
      this.$store
        .dispatch('user/toggleItemInFavorites', { itemSet, itemId })
        .then(() => {
          if (this.patchInFavorites) {
            this.$store.dispatch('app/openMenu', 'favoritesDrawer');
          }
          this.favoritesLoading = false;
        });
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../stylus/base')

.patch-product-wrapper
  display: flex
  flex-flow: row wrap
  justify-content: center
  width: 100%

  &__loading
    padding-top: 10rem
    padding-bottom: 10rem


.patch-image__wrapper
  position: relative
  display: flex
  flex-flow: column nowrap
  flex: 1 1 0
  align-items: center
  justify-content: flex-start

  padding-right: $base-padding*.5
  @media screen and (max-width: 48rem)
    flex-basis: 100%
    padding-right: 0

.patch-image
  max-width: 100%


// PATCH INFO
.patch-info
  flex: 1 1 0
  padding-left: $base-padding*.5
  @media screen and (max-width: 48rem)
    flex-basis: 100%
    padding-left: 0
  &__header-row
    display: flex
    flex-flow: row wrap
    align-items: flex-end
    justify-content: space-between
  &__name
    font-size: 1.75rem
  &__price-wrapper
    display: flex
    flex-flow: row nowrap
    align-items: flex-end
    justify-content: flex-start
    padding-bottom: $base-padding
  &__retail-price
    font-size: 1.5rem
    line-height: normal
    padding-right: $base-padding
    &--sale
      font-weight: $base-font-weight
      opacity: .5
      text-decoration: line-through
  &__sale-price
    font-size: 1.5rem
    line-height: normal
    padding-right: $base-padding
    color: $primary
    font-style: italic
    font-weight: $base-header-font-weight
  &__creator-row
    display: flex
    flex-wrap: wrap
    justify-content: space-between
    font-size: $font-size--small
    color: $accent--dark
  &__buy-row
    display: flex
    align-items: center
    justify-content: space-between
    padding-top: $base-padding
    padding-bottom: $base-padding
  &__add-to-cart
    display: flex
    flex: 1 1 100%
    margin-right: 0
    margin-left: 0

  &__description
    padding-top: $base-padding
    padding-bottom: $base-padding
    white-space: pre-line

  &__tags
    display: flex
    flex-flow: row wrap
    padding: $base-padding 0 0 0

  &__tag
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    margin-right: $base-padding
    margin-bottom: $base-padding
    padding: $base-padding*.25 $base-padding
    font-size: .9rem
    text-transform: capitalize
    text-decoration: none
    color: inherit
    background-color: $accent--light
    cursor: pointer
    &::before
      content: ''
      position: absolute
      top: 0
      left: 0
      height: 100%
      width: 100%
      z-index: 1
      background-color: $black
      opacity: 0
      transition: $base-transition
      cursor: pointer
    &:hover::before
      opacity: .12

  &__details
    list-style: none
    padding-left: 0

  &__shipping-row
    display: flex
    flex-flow: row wrap
    align-items: center
    padding: $base-padding 0
    text-align: left
    font-size: .75rem
    font-style: italic
    opacity: .65

.progress-bar
  border-radius: $base-border-radius
  &__content
    display: flex
    flex-flow: column nowrap
    align-items: flex-end
    justify-content: flex-end
    height: 100%
    padding-right: $base-padding*.5
    padding-bottom: $base-padding*.5
    font-size: $font-size--small
    text-transform: capitalize
    line-height: 1.1
    color: $secondary
  &__quantity-sold
    font-weight: $base-header-font-weight
  &__time-remaining
    font-style: italic

.patch-share
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: space-between
  width: 100%
  color: $primary
  padding-right: $base-padding

  &__url-input
    width: 1px
    opacity: 0
</style>
